import { FileUpload } from "@mui/icons-material";
import { Button, Typography, styled } from "@mui/material";
import { isReactNativeWebView, selectFileRN } from "../../../utils/useWebViewBridge";

// Input escondido para upload tradicional no browser
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface BtnFileUploadProps {
  setFileBase64: (base64: string) => void;
  accept?: string;
  label?: string;
}

/**
 * COMPONENTE DE UPLOAD DE ARQUIVOS
 * 
 * Este componente funciona em dois modos:
 * 1. Browser tradicional: Usa input file padrão
 * 2. React Native: Usa seletor nativo de arquivos
 * 
 * O que mudou do original:
 * - Detecta automaticamente o ambiente
 * - Usa seletor nativo no React Native
 * - Mantém compatibilidade com browser
 * - Mesmo resultado final (base64) em ambos os casos
 */
export function BtnFileUpload({ 
  setFileBase64, 
  accept = '.pdf',
  label = 'Enviar resultado de exame'
}: BtnFileUploadProps) {
  
  /**
   * CONVERTE ARQUIVO PARA BASE64 (BROWSER TRADICIONAL)
   * 
   * Esta função é usada apenas quando está no browser
   * No React Native, a conversão é feita nativamente
   */
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      
      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };
      
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  /**
   * MANIPULA UPLOAD NO BROWSER TRADICIONAL
   * 
   * Esta função é chamada quando o usuário seleciona um arquivo
   * usando o input file padrão do browser
   */
  const handleBrowserUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await convertToBase64(file);
      setFileBase64(base64);
    } catch (error) {
      console.error('[Upload] Erro ao converter arquivo:', error);
    }
  };

  /**
   * MANIPULA CLIQUE NO BOTÃO
   * 
   * Esta função decide qual método usar:
   * - React Native: Solicita arquivo via bridge
   * - Browser: Deixa o input padrão funcionar
   */
  const handleClick = () => {
    const isRN = isReactNativeWebView();
    console.log('[Upload] Click botão - isRN:', isRN);
    if (!isRN) {
      console.log('[Upload] Ambiente NÃO é React Native. Usando input do browser.');
    }
    if (isRN) {
      // Usa o seletor nativo do React Native
      console.log('[Upload] Enviando SELECT_FILE para RN');
      selectFileRN(accept, (result) => {
        // Quando o arquivo é selecionado, atualiza o estado
        // O resultado é o mesmo que no browser: uma string base64
        console.log('[Upload] FILE_SELECTED recebido no WebView');
        setFileBase64(result.base64);
      });
      // feedback visual para confirmar clique
      try { alert('Solicitado: abrir seletor de arquivo no app'); } catch {}
    }
    // Se não for React Native, o input padrão será acionado automaticamente
  };

  return (
    <Button
      sx={{
        width: "100%",
        height: { xs: "40px", sm: "56px" },
        textTransform: "lowercase",
        borderRadius: 3,
      }}
      component={isReactNativeWebView() ? "button" : "label"}
      role={undefined}
      variant="contained"
      tabIndex={-1}
      startIcon={<FileUpload />}
      onClick={handleClick} // ← NOVO: Handler para React Native
    >
      <Typography
        variant="body1"
        fontWeight={500}
        sx={{
          "&::first-letter": {
            textTransform: "uppercase",
          },
        }}
      >
        {label}
      </Typography>
      
      {/* Input escondido - só funciona no browser tradicional */}
      {!isReactNativeWebView() && (
        <VisuallyHiddenInput 
          accept={accept} 
          type="file"
          onChange={handleBrowserUpload}
        />
      )}
    </Button>
  );
}
